// src/services/leadershipEngine/scoreEngine.js
import { SCORE } from './score.config';
import {
  listVendas12m,
  listEventosScore,
  countsFromEventos,
  saveScoreSnapshot,

  // ✅ placas no modelo atual (imoveis.temPlaca)
  countPlacasAtivas,

  // ✅ eventos/vendas por range (mês atual)
  listEventosScoreByRange,
  listVendasByRange,
} from './scoreRepository';

/* ===========================
   Helpers
=========================== */
function clamp(n, min, max) {
  const N = Number(n || 0);
  return Math.max(min, Math.min(max, N));
}
function toNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function startOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}
function endOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

/**
 * pointsByMeta(value, meta, maxPts)
 * - Proporcional até maxPts
 * - Ex: meta=40 e maxPts=1000 => 20 gera 500 pts
 */
function pointsByMeta(countOrValue, meta, maxPts = 1000) {
  const metaN = toNumber(meta, 0);
  if (metaN <= 0) return 0;
  const v = toNumber(countOrValue, 0);
  const pts = (v / metaN) * maxPts;
  return clamp(Math.round(pts), 0, maxPts);
}

/* ============================================================
   ✅ COMISSÃO (12m)
   - usa comissao
   - fallback opcional: usa VGV * taxa quando comissao não vier
============================================================ */
function sumComissao(vendasArr) {
  const cfg = SCORE.PILARES.COMISSAO;
  let total = 0;

  (vendasArr || []).forEach((v) => {
    const com = toNumber(v?.comissao, NaN);
    if (Number.isFinite(com) && com > 0) {
      total += com;
      return;
    }

    if (cfg?.USE_FALLBACK_IF_COMISSAO_MISSING) {
      const vgv = toNumber(v?.valor, 0);
      if (vgv > 0) total += vgv * toNumber(cfg?.FALLBACK_RATE_ON_VGV, 0);
    }
  });

  return total;
}

/* ============================================================
   ✅ REGRAS MENSAIS (ELION)
   - penitência: se não houver nenhuma movimentação no mês
   - bônus full-player: se houver movimentação em todas as 5 categorias
============================================================ */
function computeElionMonthlyRules({
  movedImoveis,
  movedNegocios,
  movedPlacas,
  movedVisitas,
  movedComissao,
}) {
  const any =
    movedImoveis ||
    movedNegocios ||
    movedPlacas ||
    movedVisitas ||
    movedComissao;

  const all =
    movedImoveis &&
    movedNegocios &&
    movedPlacas &&
    movedVisitas &&
    movedComissao;

  const penalty = !any ? SCORE.ELION.PENITENCIA_SEM_MOVIMENTO_MENSAL : 0;
  const bonus = all ? SCORE.ELION.BONUS_FULL_PLAYER_MENSAL : 0;

  return {
    any,
    all,
    penalty, // (geralmente negativo)
    bonus, // (positivo)
    total: penalty + bonus,
  };
}

/* ============================================================
   ✅ SCORE 6K (OFICIAL)
   - rolling 12m: eventos_score (imoveis/negocios/visitas) + vendas (comissao)
   - placas: modelo atual (imoveis.temPlaca)
============================================================ */
export async function computeScore(uid) {
  if (!uid) throw new Error('UID não informado.');

  // 1) Rolling 12m (eventos_score) — 365d serve bem pro rolling
  const eventos12m = await listEventosScore(uid, 365);
  const counts12m = countsFromEventos(eventos12m);

  // 2) Rolling 12m (vendas -> comissão)
  const vendas12m = await listVendas12m(uid);
  const comissao12m = sumComissao(vendas12m);

  // 3) Placas (modelo atual)
  const placasAtivas = await countPlacasAtivas(uid);

  // 4) Pontos por pilar (cada um 0..1000)
  const pImoveis = pointsByMeta(
    counts12m.imoveis,
    SCORE.PILARES.IMOVEIS.META,
    1000
  );
  const pNegocios = pointsByMeta(
    counts12m.negocios,
    SCORE.PILARES.NEGOCIOS.META,
    1000
  );
  const pVisitas = pointsByMeta(
    counts12m.visitas,
    SCORE.PILARES.VISITAS.META,
    1000
  );

  // placas: estoque com placa ativa
  const pPlacas = pointsByMeta(placasAtivas, SCORE.PILARES.PLACAS.META, 1000);

  // comissão: proporcional até 1000 pts quando bater META_12M
  const pComissao = pointsByMeta(
    comissao12m,
    SCORE.PILARES.COMISSAO.META_12M,
    1000
  );

  // 5) Regras mensais (penitência / full-player) — mês calendário atual
  const from = startOfMonth(new Date());
  const to = endOfMonth(new Date());

  const [eventosMes, vendasMes] = await Promise.all([
    listEventosScoreByRange(uid, from, to),
    listVendasByRange(uid, from, to),
  ]);

  const countsMes = countsFromEventos(eventosMes);

  // placas no mês: no modelo atual não existe evento de placa,
  // então "movimentou placas" = tem ao menos 1 placa ativa.
  // (Se quiser “placa colocada no mês”, precisa evento/log.)
  const movedPlacas = placasAtivas > 0;

  const comissaoMes = sumComissao(vendasMes);

  const elionRules = computeElionMonthlyRules({
    movedImoveis: (countsMes.imoveis || 0) > 0,
    movedNegocios: (countsMes.negocios || 0) > 0,
    movedVisitas: (countsMes.visitas || 0) > 0,
    movedPlacas,
    movedComissao: comissaoMes > 0,
  });

  // 6) Total 6K
  const base6k = pImoveis + pNegocios + pPlacas + pVisitas + pComissao; // 0..5000
  const total6k = clamp(base6k + elionRules.total, 0, SCORE.MAX_TOTAL); // 0..6000

  // ✅ compat: alguns lugares ainda procuram scoreTotal/kpiTotalFinal/etc.
  const scoreTotal = total6k;

  // ✅ “movimentação do mês” (o Dashboard exibe como deltaPts e NÃO soma)
  // Aqui fica explícito: é a regra mensal (penitência/bônus).
  const movimentacaoMes = {
    total: elionRules.total,
    penalty: elionRules.penalty,
    bonus: elionRules.bonus,
    any: elionRules.any,
    all: elionRules.all,
    monthRange: { from, to },
    mes: {
      imoveis: countsMes.imoveis || 0,
      negocios: countsMes.negocios || 0,
      visitas: countsMes.visitas || 0,
      comissao: comissaoMes,
      placasAtivas,
    },
  };

  // ✅ renda: para o nível (12m)
  const renda = {
    renda12m: comissao12m,
    meta12m: SCORE.PILARES.COMISSAO.META_12M,
  };

  return {
    uid,
    mode: SCORE.MODE,
    scoreModel: SCORE.MODE,

    // ✅ OFICIAL
    total: total6k, // compat legado
    total6k,
    scoreTotal, // compat Dashboard atual
    maxTotal: SCORE.MAX_TOTAL,
    totalMax: SCORE.MAX_TOTAL, // compat extra
    updatedAt: new Date(),

    base6k,

    // ✅ pilares 6K (padrão novo)
    pilares6k: {
      imoveis: {
        pts: pImoveis,
        max: 1000,
        meta: SCORE.PILARES.IMOVEIS.META,
        count12m: counts12m.imoveis || 0,
      },
      negocios: {
        pts: pNegocios,
        max: 1000,
        meta: SCORE.PILARES.NEGOCIOS.META,
        count12m: counts12m.negocios || 0,
      },
      placas: {
        pts: pPlacas,
        max: 1000,
        meta: SCORE.PILARES.PLACAS.META,
        countAtual: placasAtivas,
      },
      visitas: {
        pts: pVisitas,
        max: 1000,
        meta: SCORE.PILARES.VISITAS.META,
        count12m: counts12m.visitas || 0,
      },
      comissao: {
        pts: pComissao,
        max: 1000,
        meta12m: SCORE.PILARES.COMISSAO.META_12M,
        total12m: comissao12m,
      },
      // ✅ pilar “Elion” (regra mensal), separado e explícito
      elion: {
        pts: elionRules.total, // pode ser negativo/positivo
        max: 1000, // (não é usado como teto, é só compat UI)
        penalty: elionRules.penalty,
        bonus: elionRules.bonus,
        any: elionRules.any,
        all: elionRules.all,
      },
    },

    // ✅ para telas/insights
    renda,
    counts12m,

    // ✅ delta do mês (pra “Movimentações do Score”)
    movimentacaoMes,

    // ✅ mantido por compat (algumas telas antigas)
    elion: {
      ...elionRules,
      monthRange: { from, to },
      mes: {
        imoveis: countsMes.imoveis || 0,
        negocios: countsMes.negocios || 0,
        visitas: countsMes.visitas || 0,
        comissao: comissaoMes,
        placasAtivas,
      },
    },
  };
}

/**
 * computeAndSaveScore(uid)
 * - calcula score 6K e salva em score/{uid}
 */
export async function computeAndSaveScore(uid) {
  if (!uid) throw new Error('UID não informado.');

  const snapshot = await computeScore(uid);

  // ✅ payload salvo (fonte oficial)
  const payloadToSave = {
    ...snapshot,
    // assinatura do modelo e totalMax (compat)
    scoreModel: SCORE.MODE,
    totalMax: SCORE.MAX_TOTAL,
    maxTotal: SCORE.MAX_TOTAL,
  };

  await saveScoreSnapshot(uid, payloadToSave);
  return payloadToSave;
}
